/**
 * The review queue's correction form (§8.4).
 *
 * "Side-by-side: extracted raw text on the left, an editable statement form on
 *  the right, pre-filled with whatever parsed. Saving marks parse_status=manual.
 *  A 'Re-parse' button exists for after a parser is fixed."
 *
 * The layout is the point: you cannot check a number without seeing the source
 * it came from, and making the user open the PDF separately guarantees they
 * won't bother.
 */

import * as Dialog from "@radix-ui/react-dialog";
import { cn } from "@/lib/cn";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Badge, Button, Field, Input, Skeleton, SELECT_CLASS } from "@/components/ui/primitives";
import {
  statementsApi,
  type Card,
  type Statement,
  type StatementCorrection,
} from "@/lib/api/client";

interface Props {
  statement: Statement;
  cards: Card[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReviewDialog({ statement, cards, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState<StatementCorrection>({
    statement_date: statement.statement_date,
    period_start: statement.period_start,
    period_end: statement.period_end,
    payment_due_date: statement.payment_due_date,
    total_amount_due: statement.total_amount_due,
    minimum_amount_due: statement.minimum_amount_due,
    credit_limit: statement.credit_limit,
    card_id: statement.card_id,
  });

  const { data: raw, isLoading: loadingText } = useQuery({
    queryKey: ["statement-text", statement.id],
    queryFn: () => statementsApi.text(statement.id),
    retry: false,
  });

  const save = useMutation({
    mutationFn: () => statementsApi.update(statement.id, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["statements"] });
      qc.invalidateQueries({ queryKey: ["review-queue"] });
      toast.success("Saved. This statement is now marked as checked by you.");
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const reparse = useMutation({
    mutationFn: () => statementsApi.reparse(statement.id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["statements"] });
      toast.success(r.message);
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const set = (k: keyof StatementCorrection) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value || null }));

  const warnings = statement.review_notes?.split("\n").filter(Boolean) ?? [];

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex h-[90vh] w-[min(72rem,95vw)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden dialog-content">
          <div className="flex items-center justify-between border-b border-hairline px-5 py-4">
            <div>
              <Dialog.Title className="font-display text-lg font-semibold text-ink">
                Check this statement
              </Dialog.Title>
              <Dialog.Description className="text-xs text-faint">
                Read by {statement.parser_name ?? "the generic reader"} · compare each value
                against the PDF text on the left
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          {/* Why it's here — specific, never "something went wrong" */}
          {warnings.length > 0 && (
            <div className="border-b border-warn/25 bg-warn/[0.06] px-5 py-3">
              <ul className="space-y-1">
                {warnings.map((w, i) => (
                  <li key={i} className="flex gap-2 text-xs text-warn">
                    <span aria-hidden>·</span>
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid min-h-0 flex-1 gap-0 md:grid-cols-2">
            {/* Left: the source */}
            <div className="min-h-0 overflow-auto border-b border-hairline bg-base/50 p-4 md:border-b-0 md:border-r">
              <span className="label-micro mb-2 block">What the PDF says</span>
              {loadingText ? (
                <div className="space-y-2">
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-4/5" />
                  <Skeleton className="h-3 w-3/5" />
                </div>
              ) : raw ? (
                <pre className="whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-muted">
                  {raw.text}
                </pre>
              ) : (
                <p className="text-sm text-faint">
                  The original PDF isn't on disk any more, so its text can't be shown. You
                  can still correct the values on the right.
                </p>
              )}
            </div>

            {/* Right: the form */}
            <div className="min-h-0 overflow-auto p-5">
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  save.mutate();
                }}
              >
                <Field label="Card">
                  <select
                    value={form.card_id ?? ""}
                    onChange={(e) => setForm((f) => ({ ...f, card_id: e.target.value }))}
                    className={cn(SELECT_CLASS, "w-full")}
                  >
                    {cards.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nickname || c.issuer} ····{c.last4}
                      </option>
                    ))}
                  </select>
                </Field>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Statement date">
                    <Input type="date" value={form.statement_date ?? ""} onChange={set("statement_date")} />
                  </Field>
                  <Field label="Payment due date">
                    <Input type="date" value={form.payment_due_date ?? ""} onChange={set("payment_due_date")} />
                  </Field>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Period start">
                    <Input type="date" value={form.period_start ?? ""} onChange={set("period_start")} />
                  </Field>
                  <Field label="Period end">
                    <Input type="date" value={form.period_end ?? ""} onChange={set("period_end")} />
                  </Field>
                </div>

                <div className="hairline-divider" />

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Total amount due" hint="Exactly as printed — don't recalculate.">
                    <Input inputMode="decimal" value={form.total_amount_due ?? ""} onChange={set("total_amount_due")} />
                  </Field>
                  <Field label="Minimum amount due">
                    <Input inputMode="decimal" value={form.minimum_amount_due ?? ""} onChange={set("minimum_amount_due")} />
                  </Field>
                </div>

                <Field label="Credit limit">
                  <Input inputMode="decimal" value={form.credit_limit ?? ""} onChange={set("credit_limit")} />
                </Field>

                <div className="flex items-center gap-2 pt-2">
                  <Badge tone="neutral">{statement.transaction_count} transactions read</Badge>
                </div>

                <p className="text-xs text-faint">
                  Saving marks this statement as checked by you, so MyDeck won't second-guess
                  it. If a parser was fixed since, re-parse instead.
                </p>

                <div className="flex justify-between gap-2 pt-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={reparse.isPending}
                    onClick={() => reparse.mutate()}
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Re-parse
                  </Button>
                  <div className="flex gap-2">
                    <Dialog.Close asChild>
                      <Button type="button" variant="ghost">
                        Cancel
                      </Button>
                    </Dialog.Close>
                    <Button type="submit" variant="primary" disabled={save.isPending}>
                      {save.isPending ? "Saving…" : "Save as checked"}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
